import { Stack, Typography } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useAtomValue } from 'jotai';
import React, { useCallback } from 'react';
import * as yup from 'yup';
import { getAgColDefFromGridField } from '../../../components/AgTable/cell-editors/AgColDefFactory';
import { useFundIdField } from '../../../data-fields/CommonFields';
import { RendererType } from '../../../data-models/field-definition';
import { sortedShareClassesStateJ } from '../../../services/state/CapTableFormStateJ';
import { useSchemaToColDef } from '../../../util/schema-utils';
import { createGridField } from '../../../view-models/grid.view-model';
import { getGroupedInvestmentsColDefs } from './investment-utils';
import { IInvestmentGridRow } from './InvestmentGridDefs';
import { SUBTOTAL_LABEL } from './OtherInvestmentsDefs';

export function useGetMyInvestmentsColDefs() {
  const shareClasses = useAtomValue(sortedShareClassesStateJ);
  const fundIdField = useFundIdField();
  const getColDefs = useSchemaToColDef();

  return useCallback((): ColDef[] => {
    const investmentDefs = getGroupedInvestmentsColDefs(shareClasses);

    const fundIdDisplayColDefs = getColDefs(
      'fundId',
      yup
        .object()
        .shape({ fundId: yup.number() })
        .meta({
          renderer: RendererType.id,
          formatter: 'fund',
        })
        .describe()
    );

    const originalCellRenderer = fundIdDisplayColDefs.cellRenderer;

    const cellRenderer = (params: CustomCellRendererProps<IInvestmentGridRow, number>) => {
      if (params.node?.footer) {
        return (
          <Stack direction={'row'} alignItems={'center'} height={'100%'}>
            <Typography variant='body2'>{SUBTOTAL_LABEL}</Typography>
          </Stack>
        );
      }
      return originalCellRenderer ? originalCellRenderer(params) : null;
    };

    return [
      {
        ...fundIdDisplayColDefs,
        ...getAgColDefFromGridField(
          createGridField({
            ...fundIdField,
            editable: true,
            formatter: 'fund',
          })
        ),
        cellRenderer,
        headerClass: 'investment-header',
      },
      ...investmentDefs,
    ];
  }, [fundIdField, getColDefs, shareClasses]);
}
